import { GridColumn } from '@progress/kendo-react-grid'
import { useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { StyledGrid } from '~/components/elements/StyledGrid'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'

import { dialogInfoAtom } from '../atoms'
import { useBrandGroup } from '../hooks/useBrandGroup'
import { useBrandGroupId } from '../hooks/useBrandGroupId'
import { useSurveyId } from '../hooks/useSurveyId'
import { pathKey } from '../utils'

import { CommandsCell } from './CommandsCell'
import { Dialogs } from './Dialogs'
import { NameCell } from './NameCell'

export const BrandList = () => {
  const brandGroupId = useBrandGroupId()
  const surveyId = useSurveyId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('settings'),
      createBreadcrumb('brandGroupList', { surveyId }),
      createBreadcrumb(pathKey, { surveyId, brandGroupId }),
    ],
    [brandGroupId, createBreadcrumb, surveyId],
  )

  const brandGroup = useBrandGroup()

  const setDialogInfo = useSetAtom(dialogInfoAtom)
  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    setDialogInfo({ dialogType: 'create' })
  }, [setDialogInfo])

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack align="start" spacing={4} verticalAppearance="stretch">
        <Typography variant="heading1">{brandGroup?.name}</Typography>

        <StyledGrid data={brandGroup?.brands}>
          <GridColumn field="number" title={t('brand_number')} />

          <GridColumn cell={NameCell} title={t('brand_name')} />

          <GridColumn cell={CommandsCell} width={22 + 25} />
        </StyledGrid>

        <StyledButton buttonStyle="success" onClick={handleAddButtonClick}>
          {t('add')}
        </StyledButton>

        <Dialogs />
      </VStack>
    </PageContainer>
  )
}
